import { Stack } from "../../../stack";
import {
    IInteractionMessage,
    InteractionMessageType,
    InteractionType,
    ITagGroup,
    MessageDirection,
    Sentiment,
    TagGroupType,
} from "@edgetier/watchtower-types";
import { useInteractionModalContext } from "../hooks";
import { groupBy } from "@edgetier/utilities";
import { formatDate, format } from "date-fns";
import { Tooltip } from "../../../tooltip";
import { Box } from "../../../box";
import { useCallback, useMemo } from "react";
import { MESSAGE_TIME_FORMAT } from "./chat.constants";
import { DayBadge } from "./day-badge";
import { StatusUpdate } from "./status-update";
import { MessageAuthor } from "./message-author";
import { MessagesList } from "./messages-list";
import { Message } from "./message";
import { LoadingChat } from "./loading-chat";

/**
 * Main chat component that renders the conversation timeline grouped by date, with messages, status updates, and day separators.
 * @returns {JSX.Element} The Chat component.
 */
export const Chat = () => {
    const {
        elements,
        interactionMessages,
        interactionMessagesAgents,
        interactionMessagesTagGroups,
        actions,
        interactionDetails,
    } = useInteractionModalContext();
    const messagesGroupedByDate = useMemo(
        () => groupBy(interactionMessages.data ?? [], ({ sendDateTime }) => format(sendDateTime, "cccc, do LLLL yyyy")),
        [interactionMessages]
    );
    const messagesAgentsNames = useMemo(
        () =>
            interactionMessagesAgents.data?.reduce<Record<string, string>>((agentNames, agent) => {
                return {
                    ...agentNames,
                    [agent.agentId]: agent.name,
                };
            }, {}) ?? {},
        [interactionMessagesAgents.data]
    );

    const tagGroupsById = useMemo(
        () =>
            interactionMessagesTagGroups.data?.reduce<Record<string, ITagGroup>>((tagGroups, tagGroup) => {
                return {
                    ...tagGroups,
                    [tagGroup.tagGroupId]: tagGroup,
                };
            }, {}) ?? {},
        [interactionMessagesTagGroups.data]
    );

    const senderName = useMemo(() => {
        return interactionDetails.data?.senderName ?? "";
    }, [interactionDetails.data]);

    const interactionType = useMemo(() => {
        return interactionDetails.data?.interactionType ?? InteractionType.Chat;
    }, [interactionDetails.data?.interactionType]);

    const getMessageAuthor = useCallback(
        (message: IInteractionMessage) => {
            if (message.direction === MessageDirection.Inbound) {
                return senderName;
            }
            if (message.isBot) {
                if (message.agentId !== null) {
                    return messagesAgentsNames[message.agentId] ?? "Automated";
                }
                return "Automated";
            }
            if (message.agentId !== null) {
                return messagesAgentsNames[message.agentId] ?? "";
            }
            return "";
        },
        [senderName, messagesAgentsNames]
    );

    return (
        <Stack justify="flex-start" w="100%" className="interaction-messages">
            {interactionMessages.isLoading && <LoadingChat />}
            {!interactionMessages.isLoading &&
                Object.entries(messagesGroupedByDate)
                    .filter(([date, messages]) =>
                        messages.some((message) => message.messageType !== InteractionMessageType.SuggestedResponse)
                    )
                    .map(([date, messages], index) => (
                        <Stack align="center" justify="flex-start" maw="100%" key={date}>
                            <DayBadge text={date} />
                            {messages.map((message, messageIndex) => {
                                if (message.messageType === InteractionMessageType.SuggestedResponse) {
                                    return null;
                                }
                                const messageIcons: Array<Sentiment | InteractionMessageType.System> = [];
                                if (message.direction === MessageDirection.Inbound) {
                                    if (message.hasFrustration) messageIcons.push(Sentiment.Frustration);
                                    if (message.hasConfusion) messageIcons.push(Sentiment.Confusion);
                                    if (message.hasGratitude) messageIcons.push(Sentiment.Gratitude);
                                    if (message.hasPraise) messageIcons.push(Sentiment.Praise);
                                } else {
                                    if (message.isBot) messageIcons.push(InteractionMessageType.System);
                                    else {
                                        if (message.hasEmpathy) messageIcons.push(Sentiment.Empathy);
                                    }
                                }

                                if (!message.interactionMessageId) return null;
                                if (message.messageType === InteractionMessageType.System) {
                                    return (
                                        <StatusUpdate
                                            key={`${date}-message-${messageIndex}`}
                                            updateText={message.message}
                                            sendDateTime={formatDate(message.sendDateTime, MESSAGE_TIME_FORMAT)}
                                        />
                                    );
                                }
                                const author = getMessageAuthor(message);
                                return (
                                    <MessagesList key={`chat-message-${messageIndex}`} direction={message.direction}>
                                        <Message
                                            messageId={message.interactionMessageId}
                                            direction={message.direction}
                                            sendDateTime={message.sendDateTime}
                                            interactionType={interactionType}
                                            author={
                                                interactionMessagesAgents.isLoading ? (
                                                    <MessageAuthor.Skeleton />
                                                ) : (
                                                    <Tooltip
                                                        label={
                                                            elements?.sentiments?.tooltipContent ? (
                                                                <elements.sentiments.tooltipContent
                                                                    sentiments={
                                                                        message.direction === MessageDirection.Outbound
                                                                            ? { hasEmpathy: message.hasEmpathy }
                                                                            : {
                                                                                  hasFrustration:
                                                                                      message.hasFrustration,
                                                                                  hasConfusion: message.hasConfusion,
                                                                                  hasGratitude: message.hasGratitude,
                                                                                  hasPraise: message.hasPraise,
                                                                              }
                                                                    }
                                                                    messageId={message.interactionMessageId}
                                                                />
                                                            ) : (
                                                                ""
                                                            )
                                                        }
                                                        disabled={
                                                            typeof elements?.sentiments?.tooltipContent ===
                                                                "undefined" ||
                                                            messageIcons.length === 0 ||
                                                            message.isBot
                                                        }
                                                    >
                                                        <Box>
                                                            <MessageAuthor
                                                                author={author}
                                                                messageIcons={messageIcons}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                )
                                            }
                                            onSentimentToggle={
                                                typeof actions?.sentiments?.onToggle === "function"
                                                    ? (sentiment) =>
                                                          actions!.sentiments!.onToggle(
                                                              message.interactionMessageId,
                                                              sentiment
                                                          )
                                                    : undefined
                                            }
                                            sentiments={
                                                message.direction === MessageDirection.Outbound
                                                    ? { hasEmpathy: message.hasEmpathy }
                                                    : {
                                                          hasFrustration: message.hasFrustration,
                                                          hasConfusion: message.hasConfusion,
                                                          hasGratitude: message.hasGratitude,
                                                          hasPraise: message.hasPraise,
                                                      }
                                            }
                                            message={message.message}
                                            time={formatDate(message.sendDateTime, MESSAGE_TIME_FORMAT)}
                                            addTagButton={
                                                elements?.tags?.addTagButton ? (
                                                    <elements.tags.addTagButton
                                                        messageId={message.interactionMessageId}
                                                    />
                                                ) : null
                                            }
                                            tags={message.tags?.map((tag) => ({
                                                ...tag,
                                                isExternal:
                                                    tagGroupsById[tag.tagGroupId]?.tagGroupType ===
                                                    TagGroupType.External,
                                            }))}
                                        />
                                    </MessagesList>
                                );
                            })}
                        </Stack>
                    ))}
        </Stack>
    );
};
